import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { purchaseApi, inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-PUR-06.01 / 02 — RFQs (request for quote) with side-by-side comparison.
 *
 * Workflow: draft → sent → quoted → awarded. Record each supplier's quote
 * as replies come in, then use compare matrix to pick the winner.
 */
export function RfqPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showRecordQuote, setShowRecordQuote] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const listQuery = useQuery({ queryKey: ['purchase', 'rfqs'], queryFn: () => purchaseApi.listRfqs() });

  const { filteredRows, kpis } = (() => {
    const rows = listQuery.data ?? [];
    let open = 0, awarded = 0, awaitingQuotes = 0;
    for (const r of rows) {
      if (['draft', 'sent', 'quoted'].includes(r.status)) open++;
      if (r.status === 'awarded') awarded++;
      if (r.status === 'sent' && r.quoted_count < r.supplier_count) awaitingQuotes++;
    }
    const filtered = statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows;
    return { filteredRows: filtered, kpis: { total: rows.length, open, awarded, awaitingQuotes } };
  })();

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'sent',      label: 'Sent' },
    { value: 'quoted',    label: 'Quoted' },
    { value: 'awarded',   label: 'Awarded' },
    { value: 'closed',    label: 'Closed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Purchase"
        title="Requests for quote"
        subtitle="Solicit quotes from multiple suppliers, compare side-by-side, award to the winner."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New RFQ
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Open" value={kpis.open} />
        <KpiTile label="Awaiting quotes" value={kpis.awaitingQuotes}
          accent={kpis.awaitingQuotes > 0 ? 'amber' : 'default'} />
        <KpiTile label="Awarded" value={kpis.awarded} accent="sage" />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
            !filteredRows.length ? (
              <EmptyState
                message={statusFilter ? `No ${statusFilter} RFQs` : 'No RFQs yet'}
                hint={statusFilter ? 'Try a different filter.' : 'Create an RFQ to gather quotes from suppliers.'}
                action={!statusFilter && (
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>
                    + New RFQ
                  </button>
                )}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>RFQ #</th>
                      <th>Title</th>
                      <th>Status</th>
                      <th className="text-right">Quotes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r: any) => {
                      const pct = r.supplier_count > 0 ? (r.quoted_count / r.supplier_count) * 100 : 0;
                      return (
                        <tr key={r.id}
                            onClick={() => setSelected(r.id)}
                            className={`cursor-pointer ${selected === r.id ? '!bg-canvas-subtle' : ''}`}>
                          <td className="font-mono text-xs font-semibold text-ink-900">{r.rfq_number}</td>
                          <td className="text-sm text-ink-900">{r.title}</td>
                          <td><StatusBadge status={r.status} /></td>
                          <td className="text-right">
                            <div className="font-mono font-semibold text-ink-900 tabular-nums text-sm">
                              {r.quoted_count}/{r.supplier_count}
                            </div>
                            {r.supplier_count > 0 && (
                              <div className="mt-1 w-16 h-1 rounded-full bg-canvas-subtle overflow-hidden ml-auto">
                                <div
                                  className={`h-full ${pct === 100 ? 'bg-sage-500' : pct > 0 ? 'bg-amber-500' : 'bg-ink-200'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select an RFQ"
              hint="Click a row on the left to view suppliers, record quotes, or compare and award."
            />
          ) : (
            <RfqDetail
              rfqId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['purchase', 'rfqs'] })}
              onRecordQuote={() => setShowRecordQuote(true)}
              onCompare={() => setShowCompare(true)}
            />
          )}
        </div>
      </div>

      {showCreate && <CreateRfqModal onClose={() => setShowCreate(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['purchase', 'rfqs'] }); setShowCreate(false); setSelected(id); }} />}

      {showRecordQuote && selected && <RecordQuoteModal rfqId={selected}
        onClose={() => setShowRecordQuote(false)}
        onRecorded={() => { setShowRecordQuote(false); qc.invalidateQueries({ queryKey: ['purchase', 'rfq', selected] }); }} />}

      {showCompare && selected && <CompareMatrixModal rfqId={selected}
        onClose={() => setShowCompare(false)}
        onAwarded={() => { setShowCompare(false); qc.invalidateQueries({ queryKey: ['purchase', 'rfqs'] }); }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    draft:     'bg-canvas-subtle text-ink-700 ring-ink-200',
    sent:      'bg-amber-50 text-amber-800 ring-amber-200',
    quoted:    'bg-blue-50 text-blue-700 ring-blue-200',
    awarded:   'bg-sage-50 text-sage-700 ring-sage-200',
    closed:    'bg-canvas-subtle text-ink-500 ring-ink-200',
    cancelled: 'bg-red-50 text-red-700 ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function RfqDetail({ rfqId, onChanged, onRecordQuote, onCompare }:
    { rfqId: string; onChanged: () => void; onRecordQuote: () => void; onCompare: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['purchase', 'rfq', rfqId], queryFn: () => purchaseApi.getRfq(rfqId) });

  const sendMut = useMutation({
    mutationFn: () => purchaseApi.sendRfq(rfqId),
    onSuccess: () => { toast.success('RFQ sent to suppliers'); qc.invalidateQueries({ queryKey: ['purchase', 'rfq', rfqId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const r = q.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{r.rfq_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{r.title}</div>
          {r.description && (
            <div className="text-xs text-ink-500 mt-1 leading-relaxed">{r.description}</div>
          )}
          {r.response_deadline && (
            <div className="text-xs text-ink-500 mt-1">
              Deadline {new Date(r.response_deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>
        <StatusBadge status={r.status} />
      </div>

      {/* Award alert */}
      {r.awarded_supplier_name && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 text-xs text-sage-800 mb-4">
          <div className="font-semibold">Awarded to {r.awarded_supplier_name}</div>
          {r.awarded_po_number && (
            <div className="mt-0.5">
              PO <span className="font-mono font-semibold">{r.awarded_po_number}</span>
            </div>
          )}
        </div>
      )}

      {/* Lines */}
      <div className="eyebrow text-ink-500 mb-2">Lines ({r.lines.length})</div>
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>Description</th>
              <th className="text-right">Qty</th>
            </tr>
          </thead>
          <tbody>
            {r.lines.map((l: any) => (
              <tr key={l.id}>
                <td className="font-mono text-2xs text-ink-500">{l.line_no}</td>
                <td>
                  <div className="font-medium text-ink-900">{l.description}</div>
                  {l.product_name && (
                    <div className="font-mono text-2xs text-ink-500 mt-0.5">
                      {l.sku} — {l.product_name}
                    </div>
                  )}
                </td>
                <td className="text-right font-mono tabular-nums">{parseFloat(l.quantity).toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Suppliers */}
      <div className="eyebrow text-ink-500 mb-2">Suppliers ({r.suppliers.length})</div>
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {r.suppliers.map((s: any) => (
              <tr key={s.rfq_supplier_id}>
                <td className="font-medium text-ink-900">{s.legal_name}</td>
                <td>
                  {s.quoted_at ? (
                    <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-sage-50 text-sage-700 ring-sage-200">
                      Quoted {new Date(s.quoted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  ) : s.declined_at ? (
                    <span className="text-danger">
                      <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200">
                        Declined
                      </span>
                      {s.decline_reason && <span className="text-ink-500 ml-2">· {s.decline_reason}</span>}
                    </span>
                  ) : (
                    <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-700 ring-ink-200">
                      Awaiting response
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {r.status === 'draft' && (
          <button className="btn-primary" disabled={sendMut.isPending} onClick={() => sendMut.mutate()}>
            {sendMut.isPending ? 'Sending…' : 'Send to suppliers'}
          </button>
        )}
        {(r.status === 'sent' || r.status === 'quoted') && (
          <>
            <button className="btn-secondary" onClick={onRecordQuote}>Record quote</button>
            <button className="btn-primary" onClick={onCompare}>Compare & award</button>
          </>
        )}
      </div>
    </div>
  );
}

function CreateRfqModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [lines, setLines] = useState<Array<{ description: string; quantity: number }>>([
    { description: '', quantity: 1 },
  ]);

  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });

  const mut = useMutation({
    mutationFn: () => purchaseApi.createRfq({
      title, description: description || undefined,
      responseDeadline: deadline || undefined,
      supplierIds: selectedSuppliers,
      lines: lines.filter((l) => l.description.trim()),
    }),
    onSuccess: (d) => { toast.success(`RFQ ${d.rfqNumber} created`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = title.trim() && selectedSuppliers.length > 0 &&
    lines.some((l) => l.description.trim());

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New request for quotation</h2>
        <p className="text-sm text-ink-500 mt-1">
          Invite multiple suppliers to quote on the same line items, then compare and award.
        </p>
      </div>

      <div className="px-6 pt-4 pb-3 space-y-3 flex-shrink-0">
        <div>
          <label className="label">Title <span className="text-danger">*</span></label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Q2 office supplies refresh" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
            <textarea className="input" rows={2} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Context for the suppliers" />
          </div>
          <div>
            <label className="label">Response deadline</label>
            <input type="date" className="input" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="px-6 pb-3 flex-shrink-0">
        <div className="eyebrow text-ink-500 mb-2">
          Suppliers to quote{selectedSuppliers.length > 0 && (
            <span className="ml-1.5 text-ink-700 font-mono tabular-nums">({selectedSuppliers.length})</span>
          )}
        </div>
        <div className="max-h-40 overflow-y-auto border border-ink-100 rounded-xl">
          {supQuery.data?.map((s: any) => (
            <label
              key={s.id}
              className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-canvas-subtle transition-colors border-b border-ink-100 last:border-b-0 cursor-pointer"
            >
              <input type="checkbox" checked={selectedSuppliers.includes(s.id)}
                onChange={(e) => setSelectedSuppliers((prev) =>
                  e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id))} />
              <span className="font-mono text-xs font-semibold text-ink-700">{s.code}</span>
              <span className="text-ink-900">{s.legal_name}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="eyebrow text-ink-500 mb-2">Lines</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="text-right w-20">Qty</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <input className="input text-xs" value={l.description}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                      placeholder="e.g. A4 printer paper, 80gsm, 500 sheets/ream" />
                  </td>
                  <td className="text-right">
                    <input type="number" min={0.01} step={1}
                      className="input w-20 text-right tabular-nums" value={l.quantity}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td>
                    <button
                      className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                      aria-label="Remove line"
                    >✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="mt-2 text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors"
          onClick={() => setLines((prev) => [...prev, { description: '', quantity: 1 }])}
        >
          + Add line
        </button>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create as draft'}
        </button>
      </div>
    </Modal>
  );
}

function RecordQuoteModal({ rfqId, onClose, onRecorded }:
    { rfqId: string; onClose: () => void; onRecorded: () => void }) {
  const q = useQuery({ queryKey: ['purchase', 'rfq', rfqId], queryFn: () => purchaseApi.getRfq(rfqId) });
  const [supplierId, setSupplierId] = useState('');
  const [mode, setMode] = useState<'quote' | 'decline'>('quote');
  const [declineReason, setDeclineReason] = useState('');
  const [quoteLines, setQuoteLines] = useState<Record<string, { unitPrice: number; leadTimeDays: number }>>({});

  const mut = useMutation({
    mutationFn: () => {
      if (mode === 'decline') {
        return purchaseApi.recordRfqQuote(rfqId, {
          supplierId, declineReason, quoteLines: [{ rfqLineId: q.data.lines[0].id, unitPrice: 0 }], // dummy; backend uses declineReason
        });
      }
      return purchaseApi.recordRfqQuote(rfqId, {
        supplierId,
        quoteLines: Object.entries(quoteLines).map(([rfqLineId, v]) => ({ rfqLineId, ...v })),
      });
    },
    onSuccess: () => { toast.success('Quote recorded'); onRecorded(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · Quote</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Record supplier quote</h2>
        <p className="text-sm text-ink-500 mt-1">
          Enter unit prices and lead times per line, or mark the supplier as declined.
        </p>
      </div>

      <div className="px-6 pt-4 pb-3 flex-shrink-0">
        <label className="label">Supplier <span className="text-danger">*</span></label>
        <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
          <option value="">Select supplier from invited list…</option>
          {q.data?.suppliers.map((s: any) => (
            <option key={s.supplier_id} value={s.supplier_id}>{s.legal_name}</option>
          ))}
        </select>
      </div>

      <div className="px-6 pb-3 flex-shrink-0 flex gap-2">
        <button
          type="button"
          onClick={() => setMode('quote')}
          className={`flex-1 px-4 py-2 text-sm font-semibold rounded-pill transition-all ${
            mode === 'quote'
              ? 'bg-canvas-subtle text-ink-900 ring-1 ring-inset ring-ink-200'
              : 'text-ink-600 hover:bg-canvas-subtle'
          }`}
        >Record quote</button>
        <button
          type="button"
          onClick={() => setMode('decline')}
          className={`flex-1 px-4 py-2 text-sm font-semibold rounded-pill transition-all ${
            mode === 'decline'
              ? 'bg-red-50 text-danger ring-1 ring-inset ring-red-200'
              : 'text-ink-600 hover:bg-canvas-subtle'
          }`}
        >Mark declined</button>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        {mode === 'decline' ? (
          <div>
            <label className="label">Decline reason</label>
            <textarea className="input" rows={3} value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. Unable to meet response deadline" />
          </div>
        ) : (
          <>
            <div className="eyebrow text-ink-500 mb-2">Line pricing</div>
            <div className="border border-ink-100 rounded-xl overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th className="text-right w-28">Unit price</th>
                    <th className="text-right w-28">Lead time (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data?.lines.map((l: any) => {
                    const v = quoteLines[l.id] ?? { unitPrice: 0, leadTimeDays: 0 };
                    return (
                      <tr key={l.id}>
                        <td>
                          <div className="font-medium text-ink-900 text-xs">{l.description}</div>
                          <div className="text-2xs text-ink-500 mt-0.5 font-mono tabular-nums">
                            qty {parseFloat(l.quantity).toFixed(0)}
                          </div>
                        </td>
                        <td className="text-right">
                          <input type="number" min={0} step={0.01}
                            className="input w-24 text-right tabular-nums" value={v.unitPrice}
                            onChange={(e) => setQuoteLines((prev) => ({ ...prev, [l.id]: { ...v, unitPrice: parseFloat(e.target.value) || 0 } }))} />
                        </td>
                        <td className="text-right">
                          <input type="number" min={0} step={1}
                            className="input w-20 text-right tabular-nums" value={v.leadTimeDays}
                            onChange={(e) => setQuoteLines((prev) => ({ ...prev, [l.id]: { ...v, leadTimeDays: parseInt(e.target.value, 10) || 0 } }))} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!supplierId || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

function CompareMatrixModal({ rfqId, onClose, onAwarded }:
    { rfqId: string; onClose: () => void; onAwarded: () => void }) {
  const q = useQuery({ queryKey: ['purchase', 'rfq-compare', rfqId], queryFn: () => purchaseApi.compareRfq(rfqId) });
  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const [awardSupplier, setAwardSupplier] = useState('');
  const [createPo, setCreatePo] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');

  const awardMut = useMutation({
    mutationFn: () => purchaseApi.awardRfq(rfqId, {
      supplierId: awardSupplier,
      createPo,
      warehouseId: createPo ? warehouseId : undefined,
    }),
    onSuccess: (d) => { toast.success(d.poNumber ? `Awarded. PO ${d.poNumber} created as draft.` : 'Awarded.'); onAwarded(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;
  const c = q.data;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-3 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · Compare</div>
        <h2 className="text-xl font-bold font-display text-ink-900 truncate">{c.rfq.title}</h2>
        <p className="text-sm text-ink-500 mt-1">
          Green = best price for that line. Totals shown per supplier. Select a winner and optionally create a draft PO.
        </p>
      </div>

      <div className="px-6 flex-1 overflow-auto min-h-0">
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-canvas-subtle z-10">Line</th>
                <th className="text-right">Qty</th>
                {c.quotes.map((sq: any) => (
                  <th key={sq.supplierId} className="text-center min-w-[140px]">{sq.supplierName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.lines.map((line: any) => (
                <tr key={line.rfqLineId}>
                  <td className="sticky left-0 bg-canvas-elevated z-10 text-xs">
                    <div className="font-medium text-ink-900">{line.description}</div>
                  </td>
                  <td className="text-right font-mono text-xs tabular-nums">{line.quantity.toFixed(0)}</td>
                  {c.quotes.map((sq: any) => {
                    const lq = sq.lineQuotes.find((x: any) => x.rfqLineId === line.rfqLineId);
                    if (!lq) return (
                      <td key={sq.supplierId} className="text-center text-xs text-ink-400">—</td>
                    );
                    return (
                      <td key={sq.supplierId}
                        className={`text-center ${lq.isBest ? 'bg-sage-50' : ''}`}>
                        <div className={`text-sm font-mono tabular-nums ${lq.isBest ? 'text-sage-700 font-bold' : 'text-ink-900'}`}>
                          ${lq.unitPrice.toFixed(2)}
                        </div>
                        {lq.leadTimeDays != null && (
                          <div className="text-2xs text-ink-500 font-mono tabular-nums">{lq.leadTimeDays}d</div>
                        )}
                        {!lq.isBest && lq.deltaVsBest > 0 && (
                          <div className="text-2xs text-danger font-mono tabular-nums">
                            +${lq.deltaVsBest.toFixed(2)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="font-semibold border-t-2 border-ink-300">
                <td colSpan={2} className="sticky left-0 bg-canvas-subtle z-10 text-right text-xs eyebrow text-ink-700">
                  Total quoted value
                </td>
                {c.quotes.map((sq: any) => (
                  <td key={sq.supplierId} className="text-center font-mono tabular-nums text-ink-900">
                    ${sq.totalValue.toFixed(2)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex-shrink-0">
        <div className="eyebrow text-ink-700 mb-3">Award to</div>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="label">Winning supplier <span className="text-danger">*</span></label>
            <select className="input" value={awardSupplier} onChange={(e) => setAwardSupplier(e.target.value)}>
              <option value="">Select winner…</option>
              {c.quotes.map((sq: any) => (
                <option key={sq.supplierId} value={sq.supplierId} disabled={!sq.quotedAt}>
                  {sq.supplierName}{sq.quotedAt ? ` ($${sq.totalValue.toFixed(2)})` : ' — no quote recorded yet'}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-700 h-[38px]">
            <input type="checkbox" checked={createPo} onChange={(e) => setCreatePo(e.target.checked)} />
            Also create draft PO
          </label>
          {createPo && (
            <div>
              <label className="label">Warehouse <span className="text-danger">*</span></label>
              <select className="input min-w-[160px]" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">Select…</option>
                {whQuery.data?.map((w: any) => <option key={w.id} value={w.id}>{w.code}</option>)}
              </select>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <button className="btn-secondary" onClick={onClose}>Close</button>
            <button className="btn-primary"
              disabled={!awardSupplier || (createPo && !warehouseId) || awardMut.isPending}
              onClick={() => awardMut.mutate()}>
              {awardMut.isPending ? 'Awarding…' : 'Award'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
